/**
 * ClipboardSerializer — TipTap Extension
 *
 * Intercepts copy and cut events to write rich HTML (with inline styles)
 * to the clipboard. This ensures formatting carries over when pasting
 * into Word, Google Docs, and other rich-text applications.
 *
 * On paste, splits line breaks inside pasted paragraphs into separate
 * paragraphs, so each transcript line stays its own numberable line.
 *
 * Uses the serializeToRichHTML utility, which reads from the centralized
 * transcriptStyles config.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Slice } from '@tiptap/pm/model';
import serializeToRichHTML from '../utils/serializeToRichHTML';

const clipboardKey = new PluginKey('clipboardSerializer');

/**
 * Handle a copy or cut event by serializing the selection to rich HTML.
 * `config` is the active style config (reflecting the user's line styles);
 * when null, serializeToRichHTML falls back to the shipped defaults.
 */
function writeRichClipboard(view, event, isCut, config) {
  const { state } = view;
  const { selection } = state;

  // If nothing is selected, let the browser handle it
  if (selection.empty) return false;

  // Get the selected content as a ProseMirror Slice
  const slice = state.doc.slice(selection.from, selection.to);

  // Serialize the fragment to rich HTML with inline styles
  const html = serializeToRichHTML(slice.content, config ?? undefined);

  // Build plain text fallback (newline between paragraphs)
  let plainText = '';
  slice.content.forEach((node) => {
    if (plainText.length > 0) plainText += '\n';
    plainText += node.textContent;
  });

  // Write both representations to the clipboard
  event.preventDefault();
  event.clipboardData.setData('text/html', html);
  event.clipboardData.setData('text/plain', plainText);

  // For cut: delete the selected content from the editor
  if (isCut) {
    view.dispatch(state.tr.deleteSelection());
  }

  return true;
}

/**
 * Split every pasted paragraph at its hard breaks. Text copied from a code
 * block or <pre> (e.g. a .txt artifact) arrives as one paragraph with a hard
 * break per line; Ript numbers and classifies whole paragraphs, so each
 * transcript line needs to be its own paragraph. Leading spaces are kept.
 */
export function splitHardBreaks(fragment) {
  const nodes = [];
  fragment.forEach((node) => {
    let hasBreak = false;
    node.forEach((child) => {
      if (child.type.name === 'hardBreak') hasBreak = true;
    });
    if (node.type.name !== 'paragraph' || !hasBreak) {
      nodes.push(node);
      return;
    }

    // Each piece keeps the line type; only the first keeps the margin
    // prefix, since that is the only piece that actually starts with it.
    const lines = [[]];
    node.forEach((child) => {
      if (child.type.name === 'hardBreak') lines.push([]);
      else lines[lines.length - 1].push(child);
    });
    lines.forEach((items, i) => {
      const attrs = i === 0 ? node.attrs : { ...node.attrs, marginContent: '' };
      nodes.push(node.type.create(attrs, items));
    });
  });
  return Fragment.from(nodes);
}

const ClipboardSerializer = Extension.create({
  name: 'clipboardSerializer',

  // Holds the active style config, kept in sync by Editor.jsx as the user's
  // line styles change. Read at copy/cut time so exports stay current.
  addStorage() {
    return { config: null };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin({
        key: clipboardKey,
        props: {
          transformPasted(slice) {
            return new Slice(splitHardBreaks(slice.content), slice.openStart, slice.openEnd);
          },
          handleDOMEvents: {
            copy(view, event) {
              return writeRichClipboard(view, event, false, storage.config);
            },
            cut(view, event) {
              return writeRichClipboard(view, event, true, storage.config);
            },
          },
        },
      }),
    ];
  },
});

export default ClipboardSerializer;
